import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  TableSortLabel,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  Checkbox,
  InputAdornment,
  Radio,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import { RecordItem } from './BusinessMapTab';
import { bandText, effectiveRate, formatRate, isBanded } from '../../utils/bandRate';

// A tank mix lists one line per product in each of the Product/Rate/Units
// columns — nowrap keeps those lines level across the three columns.
const productLineSx = { whiteSpace: 'nowrap' } as const;

const formatDate = (iso: string) => new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });

// GetRecords' sortBy values — sorted server-side, so it holds across pages.
type SortColumn = 'farm' | 'field' | 'area' | 'band' | 'date' | 'product' | 'rate' | 'units' | 'type' | 'method' | 'geneStack' | 'notes' | 'region';

interface Props {
  businessId: string;
  authHeader: { Authorization: string };
  isAdmin: boolean;
  rootFeatureId: string | null;
  rootFeatureName: string | null;
  // Bumped on every Map-tab double-click, so the same feature can be
  // re-applied after its filter chip was cleared.
  rootFeatureKey?: number;
  onSelectRecord: (record: RecordItem) => void;
  // Bumped by the parent after it saves a record, to refetch this table.
  refreshKey?: number;
}

interface TimePeriodOption {
  id: string;
  name: string;
}

const toIso = (localDateTime: string): string | undefined => (localDateTime ? new Date(localDateTime).toISOString() : undefined);

// General paged/filterable browser for a business's records — a location
// filter (a farm picked from the Farm dropdown, or a feature double-clicked
// on the Map tab) is one filter among several rather than a gate: with no
// filters at all, this shows the most recent records for the business,
// latest first. The parent keeps this panel mounted while the Map tab is
// showing, so a double-click arrives as a rootFeatureKey change rather
// than a remount; the filter can be cleared locally afterward without
// affecting the Map tab's own selection.
const OperationsPanel: React.FC<Props> = ({ businessId, authHeader, isAdmin, rootFeatureId, rootFeatureName, rootFeatureKey, onSelectRecord, refreshKey }) => {
  const [locationFilter, setLocationFilter] = useState<{ id: string; name: string } | null>(
    rootFeatureId ? { id: rootFeatureId, name: rootFeatureName ?? '' } : null
  );
  const [includeDescendants, setIncludeDescendants] = useState(true);
  const [farms, setFarms] = useState<{ id: string; name: string; businessName: string }[]>([]);
  const [fields, setFields] = useState<{ id: string; name: string; farmId?: string; farmName?: string; businessName: string }[]>([]);
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const [timePeriodId, setTimePeriodId] = useState('');
  const [timePeriods, setTimePeriods] = useState<TimePeriodOption[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [records, setRecords] = useState<RecordItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0); // MUI TablePagination is 0-indexed
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState<SortColumn>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<RecordItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Ids the user has checked, potentially spanning several pages ("select
  // this page" adds the currently-loaded page's ids; "select all N matching"
  // below replaces this with every id the current filters match, fetched
  // unpaged from the server). allFilteredSelected just tracks which of those
  // two states we're in, for the toolbar's wording/button — the ids
  // themselves are the single source of truth for what gets deleted.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [allFilteredSelected, setAllFilteredSelected] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Group: the ticked records become one operation (a tank mix imported as
  // a record per product). Split: product lines move off one record into a
  // new one, or one each. See RecordsController.GroupRecords / SplitRecord.
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupKeepId, setGroupKeepId] = useState<string | null>(null);
  const [splitTarget, setSplitTarget] = useState<RecordItem | null>(null);
  const [splitLineIds, setSplitLineIds] = useState<Set<string>>(new Set());
  const [regrouping, setRegrouping] = useState(false);
  const [regroupError, setRegroupError] = useState<string | null>(null);

  const fetchTimePeriods = async () => {
    try {
      const res = await fetch('/api/time-periods', { headers: authHeader });
      if (res.ok) setTimePeriods(await res.json());
    } catch {
      /* ignore */
    }
  };

  // Shared with selectAllMatchingFilter below, so "select all N matching"
  // asks the server for exactly the same set fetchRecords is paging through.
  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    const fromIso = toIso(rangeFrom);
    const toIsoVal = toIso(rangeTo);
    if (fromIso) params.set('from', fromIso);
    if (toIsoVal) params.set('to', toIsoVal);
    if (timePeriodId) params.set('timePeriodId', timePeriodId);
    if (search) params.set('search', search);
    if (locationFilter) {
      params.set('spatialFeatureId', locationFilter.id);
      params.set('includeDescendants', String(includeDescendants));
    }
    return params;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeFrom, rangeTo, timePeriodId, search, locationFilter, includeDescendants]);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = buildFilterParams();
      params.set('page', String(page + 1));
      params.set('pageSize', String(pageSize));
      params.set('sortBy', sortBy);
      params.set('sortDir', sortDir);
      const res = await fetch(`/api/businesses/${businessId}/records?${params}`, { headers: authHeader });
      if (!res.ok) throw new Error('Failed to load operations');
      const data = await res.json();
      setRecords(data.items);
      setTotalCount(data.totalCount);
    } catch {
      setError('Failed to load operations');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, page, pageSize, sortBy, sortDir, buildFilterParams, refreshKey]);

  // Date starts newest-first; text/number columns start A→Z / low→high.
  const handleSort = (column: SortColumn) => {
    if (sortBy === column) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir(column === 'date' ? 'desc' : 'asc');
    }
    setPage(0);
  };

  const sortableHeader = (column: SortColumn, label: string, align?: 'right') => (
    <TableCell align={align} sortDirection={sortBy === column ? sortDir : false}>
      <TableSortLabel active={sortBy === column} direction={sortBy === column ? sortDir : 'asc'} onClick={() => handleSort(column)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  useEffect(() => {
    fetchTimePeriods();
    fetch(`/api/businesses/${businessId}/records/farms`, { headers: authHeader })
      .then((res) => (res.ok ? res.json() : []))
      .then(setFarms)
      .catch(() => setFarms([]));
    fetch(`/api/businesses/${businessId}/records/fields`, { headers: authHeader })
      .then((res) => (res.ok ? res.json() : []))
      .then(setFields)
      .catch(() => setFields([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  useEffect(() => {
    if (!rootFeatureKey || !rootFeatureId) return;
    setLocationFilter({ id: rootFeatureId, name: rootFeatureName ?? '' });
    setIncludeDescendants(true);
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootFeatureKey]);

  // Farms from child businesses are listed too (records are, by default),
  // so the business name tells same-named farms apart.
  const farmBusinessCount = new Set(farms.map((f) => f.businessName)).size;
  // The Farm and Field dropdowns both drive the one location filter: a
  // field selected (from the dropdown or a Map double-click) also shows
  // its farm, and the Field list narrows to the selected farm's fields.
  const selectedField = fields.find((f) => f.id === locationFilter?.id);
  const selectedFarmId = farms.some((f) => f.id === locationFilter?.id) ? locationFilter!.id : selectedField?.farmId ?? '';
  const naturalCompare = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  const fieldOptions = (selectedFarmId ? fields.filter((f) => f.farmId === selectedFarmId) : fields)
    .slice()
    .sort((a, b) => naturalCompare(a.farmName ?? '', b.farmName ?? '') || naturalCompare(a.name, b.name));
  const handleFarmChange = (farmId: string) => {
    const farm = farms.find((f) => f.id === farmId);
    setLocationFilter(farm ? { id: farm.id, name: farm.name } : null);
    setIncludeDescendants(true);
    setPage(0);
  };
  const handleFieldChange = (fieldId: string) => {
    const field = fields.find((f) => f.id === fieldId);
    const farm = farms.find((f) => f.id === selectedFarmId);
    setLocationFilter(
      field ? { id: field.id, name: field.farmName ? `${field.farmName} / ${field.name}` : field.name }
        : farm ? { id: farm.id, name: farm.name }
        : null
    );
    setIncludeDescendants(true);
    setPage(0);
  };

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Debounce free-text search so it doesn't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // A selection is scoped to "whatever the current filters match" — once
  // those change, ids picked under the old filters no longer mean anything.
  useEffect(() => {
    setSelectedIds(new Set());
    setAllFilteredSelected(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, rangeFrom, rangeTo, timePeriodId, search, locationFilter, includeDescendants]);

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`/api/records/${deleteTarget.id}/hard`, { method: 'DELETE', headers: authHeader });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to delete record');
      }
      setRecords((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setTotalCount((prev) => Math.max(0, prev - 1));
      setDeleteTarget(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete record');
    } finally {
      setDeleting(false);
    }
  };

  const resetFilters = () => {
    setRangeFrom('');
    setRangeTo('');
    setTimePeriodId('');
    setLocationFilter(null);
    setIncludeDescendants(true);
    setSearchInput('');
    setSearch('');
    setPage(0);
  };

  const pageIds = records.map((r) => r.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const somePageSelected = pageIds.some((id) => selectedIds.has(id));

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
    setAllFilteredSelected(false);
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setAllFilteredSelected(false);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setAllFilteredSelected(false);
  };

  const selectAllMatchingFilter = async () => {
    setSelectingAll(true);
    try {
      const params = buildFilterParams();
      const res = await fetch(`/api/businesses/${businessId}/records/ids?${params}`, { headers: authHeader });
      if (!res.ok) throw new Error('Failed to select all records');
      const ids: string[] = await res.json();
      setSelectedIds(new Set(ids));
      setAllFilteredSelected(true);
    } catch {
      setError('Failed to select all matching records');
    } finally {
      setSelectingAll(false);
    }
  };

  // Group and Split work on the records loaded on this page.
  const selectedRecords = records
    .filter((r) => selectedIds.has(r.id))
    .sort((a, b) => a.timeFrom.localeCompare(b.timeFrom));
  const allSelectedLoaded = selectedRecords.length === selectedIds.size;
  const groupBandsDiffer = new Set(selectedRecords.map((r) => r.bandPercent ?? 100)).size > 1;
  const splitCandidate = selectedIds.size === 1 && selectedRecords.length === 1 && selectedRecords[0].products.length >= 2 ? selectedRecords[0] : null;

  const openGroup = () => {
    setGroupKeepId(selectedRecords[0]?.id ?? null);
    setRegroupError(null);
    setGroupOpen(true);
  };

  const openSplit = () => {
    setSplitTarget(splitCandidate);
    setSplitLineIds(new Set());
    setRegroupError(null);
  };

  const postRegroup = async (url: string, body: unknown) => {
    setRegrouping(true);
    setRegroupError(null);
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to update records');
      }
      setGroupOpen(false);
      setSplitTarget(null);
      clearSelection();
      await fetchRecords();
    } catch (e) {
      setRegroupError(e instanceof Error ? e.message : 'Failed to update records');
    } finally {
      setRegrouping(false);
    }
  };

  const handleGroup = () =>
    postRegroup('/api/records/group', { recordIds: selectedRecords.map((r) => r.id), keepRecordId: groupKeepId });

  const handleSplit = (eachOwnRecord: boolean) =>
    splitTarget && postRegroup(`/api/records/${splitTarget.id}/split`, { lineIds: Array.from(splitLineIds), eachOwnRecord });

  const toggleSplitLine = (id: string) =>
    setSplitLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const handleBulkDeleteConfirmed = async () => {
    setBulkDeleting(true);
    try {
      const res = await fetch('/api/records/bulk-delete', {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify(Array.from(selectedIds)),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to delete records');
      }
      setBulkDeleteOpen(false);
      clearSelection();
      await fetchRecords();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete records');
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <Box sx={{ p: 2, flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6">Operations</Typography>
        {locationFilter && (
          <>
            <Chip size="small" label={locationFilter.name} onDelete={() => { setLocationFilter(null); setPage(0); }} />
            <FormControlLabel
              sx={{ ml: 0.5 }}
              control={
                <Switch
                  size="small"
                  checked={includeDescendants}
                  onChange={(e) => { setIncludeDescendants(e.target.checked); setPage(0); }}
                />
              }
              label="Include children"
            />
          </>
        )}
        <IconButton size="small" onClick={fetchRecords} title="Refresh"><RefreshIcon fontSize="small" /></IconButton>
        <Box sx={{ flexGrow: 1 }} />
        <TextField
          size="small"
          placeholder="Search farm, products…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="records-farm-label">Farm</InputLabel>
          <Select
            labelId="records-farm-label"
            label="Farm"
            value={selectedFarmId}
            onChange={(e) => handleFarmChange(e.target.value)}
          >
            <MenuItem value=""><em>Any</em></MenuItem>
            {farms.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {farmBusinessCount > 1 ? `${f.name} (${f.businessName})` : f.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="records-field-label">Field</InputLabel>
          <Select
            labelId="records-field-label"
            label="Field"
            value={selectedField?.id ?? ''}
            onChange={(e) => handleFieldChange(e.target.value)}
          >
            <MenuItem value=""><em>Any</em></MenuItem>
            {fieldOptions.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {selectedFarmId || !f.farmName ? f.name : `${f.name} — ${f.farmName}${farmBusinessCount > 1 ? ` (${f.businessName})` : ''}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="records-time-period-label">Time Period</InputLabel>
          <Select
            labelId="records-time-period-label"
            label="Time Period"
            value={timePeriodId}
            onChange={(e) => { setTimePeriodId(e.target.value); setPage(0); }}
          >
            <MenuItem value=""><em>Any</em></MenuItem>
            {timePeriods.map((tp) => (
              <MenuItem key={tp.id} value={tp.id}>{tp.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          type="datetime-local"
          label="From"
          value={rangeFrom}
          onChange={(e) => { setRangeFrom(e.target.value); setPage(0); }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          type="datetime-local"
          label="To"
          value={rangeTo}
          onChange={(e) => { setRangeTo(e.target.value); setPage(0); }}
          InputLabelProps={{ shrink: true }}
        />
        <IconButton size="small" onClick={resetFilters} title="Clear filters">
          <RestartAltIcon fontSize="small" />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {isAdmin && selectedIds.size > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 1, bgcolor: 'action.selected', borderRadius: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2">
            {allFilteredSelected
              ? `All ${selectedIds.size} matching record${selectedIds.size === 1 ? '' : 's'} selected.`
              : `${selectedIds.size} selected.`}
          </Typography>
          {!allFilteredSelected && allPageSelected && totalCount > records.length && (
            <Button size="small" onClick={selectAllMatchingFilter} disabled={selectingAll}>
              {selectingAll ? 'Selecting…' : `Select all ${totalCount} matching`}
            </Button>
          )}
          <Button size="small" onClick={clearSelection}>Clear selection</Button>
          <Box sx={{ flexGrow: 1 }} />
          {selectedIds.size >= 2 && (
            <Tooltip title={allSelectedLoaded ? 'Make these one operation (a tank mix)' : 'Grouping works on records on this page'}>
              <span>
                <Button size="small" variant="outlined" startIcon={<CallMergeIcon />} onClick={openGroup} disabled={!allSelectedLoaded}>
                  Group Selected
                </Button>
              </span>
            </Tooltip>
          )}
          {splitCandidate && (
            <Button size="small" variant="outlined" startIcon={<CallSplitIcon />} onClick={openSplit}>
              Split
            </Button>
          )}
          <Button size="small" color="error" variant="contained" startIcon={<DeleteIcon />} onClick={() => setBulkDeleteOpen(true)}>
            Delete Selected
          </Button>
        </Box>
      )}

      {loading && records.length === 0 ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress size={24} /></Box>
      ) : records.length === 0 ? (
        <Typography color="text.secondary">No operations found.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table
            size="small"
            sx={{
              // Compact rows: imports run to hundreds of records per business.
              '& .MuiTableCell-root': { py: 0.25, fontSize: '0.8rem', lineHeight: 1.35 },
              '& .MuiCheckbox-root, & .MuiIconButton-root': { p: 0.25 },
            }}
          >
            <TableHead>
              <TableRow>
                {isAdmin && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      indeterminate={somePageSelected && !allPageSelected}
                      checked={allPageSelected}
                      onChange={toggleSelectAllOnPage}
                    />
                  </TableCell>
                )}
                {sortableHeader('farm', 'Farm')}
                {sortableHeader('field', 'Field')}
                {sortableHeader('area', 'Ha', 'right')}
                {sortableHeader('date', 'Date')}
                {sortableHeader('product', 'Product')}
                {sortableHeader('rate', 'Application rate', 'right')}
                {sortableHeader('units', 'Units')}
                {sortableHeader('band', 'Band', 'right')}
                {/* Worked out per line from the rate and band, so not a
                    server-side sort column. */}
                <TableCell align="right">Effective rate</TableCell>
                {sortableHeader('notes', 'Notes')}
                {isAdmin && <TableCell align="right" />}
                {sortableHeader('type', 'Type')}
                {sortableHeader('method', 'Method')}
                {sortableHeader('geneStack', 'Gene Stack')}
                {/* From the field season on the record's date, so not a
                    server-side sort column. */}
                <TableCell>Irrigation</TableCell>
                {sortableHeader('region', 'Region')}
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((op) => (
                <TableRow key={op.id} hover selected={selectedIds.has(op.id)} sx={{ cursor: 'pointer' }} onClick={() => onSelectRecord(op)}>
                  {isAdmin && (
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox size="small" checked={selectedIds.has(op.id)} onChange={() => toggleSelectOne(op.id)} />
                    </TableCell>
                  )}
                  <TableCell>{op.farmName ?? '—'}</TableCell>
                  <TableCell>{op.fieldName ?? op.spatialFeatureName ?? op.locationName ?? '—'}</TableCell>
                  <TableCell align="right">
                    {op.effectiveAreaHectares != null ? op.effectiveAreaHectares.toLocaleString('en-AU', { maximumFractionDigits: 2 }) : '—'}
                    {isBanded(op.bandPercent) && op.affectedAreaHectares != null && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ whiteSpace: 'nowrap' }}>
                        {op.affectedAreaHectares.toLocaleString('en-AU', { maximumFractionDigits: 2 })} treated
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{new Date(op.timeFrom).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}</TableCell>
                  <TableCell>
                    {op.products.length > 0 ? op.products.map((p) => <Box key={p.id} sx={productLineSx}>{p.productName}</Box>) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {op.products.length > 0 ? op.products.map((p) => <Box key={p.id} sx={productLineSx}>{p.rate}</Box>) : '—'}
                  </TableCell>
                  <TableCell>
                    {op.products.length > 0 ? op.products.map((p) => <Box key={p.id} sx={productLineSx}>{p.rateUnitName}</Box>) : '—'}
                  </TableCell>
                  <TableCell align="right">{bandText(op.bandPercent)}</TableCell>
                  <TableCell align="right">
                    {op.products.length > 0
                      ? op.products.map((p) => <Box key={p.id} sx={productLineSx}>{formatRate(effectiveRate(p.rate, op.bandPercent))}</Box>)
                      : '—'}
                  </TableCell>
                  <TableCell sx={{ minWidth: 200 }}>{op.notes ?? '—'}</TableCell>
                  {isAdmin && (
                    <TableCell align="right">
                      <Tooltip title="Delete permanently">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget(op); }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                  <TableCell>{op.operationTypeName}</TableCell>
                  <TableCell>{op.operationMethodName ?? '—'}</TableCell>
                  <TableCell>{op.geneStackName ?? '—'}</TableCell>
                  <TableCell>{op.irrigationTypeName ?? '—'}</TableCell>
                  <TableCell>
                    {/* The linked Region (what regional reports group by);
                        source text that never matched a Region is shown
                        as such, to be linked in the edit dialog. */}
                    {op.linkedRegionName ?? (op.regionName ? (
                      <Tooltip title="Not linked to a Region — open the record to pick one">
                        <Box component="span" sx={{ color: 'warning.main', fontStyle: 'italic' }}>{op.regionName} (not linked)</Box>
                      </Tooltip>
                    ) : '—')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </TableContainer>
      )}

      <Dialog open={groupOpen} onClose={() => !regrouping && setGroupOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Group {selectedRecords.length} Records into One Operation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Every product goes onto the record you keep, which keeps its own date, area, band, operation type and notes. The
            others are deleted, and their import rows point at the kept record. Split it again if this was wrong.
          </Typography>
          {new Set(selectedRecords.map((r) => formatDate(r.timeFrom))).size > 1 && (
            <Alert severity="info" sx={{ mb: 1 }}>These are on different dates; the kept record&apos;s date is used.</Alert>
          )}
          {groupBandsDiffer && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              These have different bands, so they can&apos;t be one record: the band applies to every product on it.
            </Alert>
          )}
          {regroupError && <Alert severity="error" sx={{ mb: 1 }}>{regroupError}</Alert>}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">Keep</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Field</TableCell>
                <TableCell align="right">Ha</TableCell>
                <TableCell align="right">Band</TableCell>
                <TableCell>Products</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedRecords.map((r) => (
                <TableRow key={r.id} hover selected={r.id === groupKeepId} sx={{ cursor: 'pointer' }} onClick={() => setGroupKeepId(r.id)}>
                  <TableCell padding="checkbox"><Radio size="small" checked={r.id === groupKeepId} /></TableCell>
                  <TableCell>{formatDate(r.timeFrom)}</TableCell>
                  <TableCell>{r.fieldName ?? r.spatialFeatureName ?? r.locationName ?? '—'}</TableCell>
                  <TableCell align="right">{r.effectiveAreaHectares?.toLocaleString('en-AU', { maximumFractionDigits: 2 }) ?? '—'}</TableCell>
                  <TableCell align="right">{bandText(r.bandPercent)}</TableCell>
                  <TableCell>
                    {r.products.map((p) => <Box key={p.id} sx={productLineSx}>{p.productName} {p.rate} {p.rateUnitName}</Box>)}
                  </TableCell>
                  <TableCell>{r.operationTypeName}</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>{r.notes ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupOpen(false)} disabled={regrouping}>Cancel</Button>
          <Button onClick={handleGroup} variant="contained" startIcon={<CallMergeIcon />} disabled={regrouping || !groupKeepId || groupBandsDiffer}>
            {regrouping ? 'Grouping…' : 'Group'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!splitTarget} onClose={() => !regrouping && setSplitTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Split Record</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Tick the products to move off this {splitTarget?.operationTypeName.toLowerCase()} record
            {splitTarget ? ` (${formatDate(splitTarget.timeFrom)}${splitTarget.fieldName ? `, ${splitTarget.fieldName}` : ''})` : ''}. The new
            record copies its date, field, area, band, type and notes. At least one product stays.
          </Typography>
          {regroupError && <Alert severity="error" sx={{ mb: 1 }}>{regroupError}</Alert>}
          {splitTarget?.products.map((p) => (
            <FormControlLabel
              key={p.id}
              sx={{ display: 'flex' }}
              control={<Checkbox size="small" checked={splitLineIds.has(p.id)} onChange={() => toggleSplitLine(p.id)} />}
              label={`${p.productName} ${p.rate} ${p.rateUnitName}`}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSplitTarget(null)} disabled={regrouping}>Cancel</Button>
          <Button
            onClick={() => handleSplit(true)}
            disabled={regrouping || splitLineIds.size === 0 || splitLineIds.size >= (splitTarget?.products.length ?? 0)}
          >
            One record each
          </Button>
          <Button
            onClick={() => handleSplit(false)}
            variant="contained"
            startIcon={<CallSplitIcon />}
            disabled={regrouping || splitLineIds.size === 0 || splitLineIds.size >= (splitTarget?.products.length ?? 0)}
          >
            {regrouping ? 'Splitting…' : 'Move to a new record'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDeleteOpen} onClose={() => setBulkDeleteOpen(false)}>
        <DialogTitle>Delete {selectedIds.size} Record{selectedIds.size === 1 ? '' : 's'}</DialogTitle>
        <DialogContent>
          <Typography>
            Permanently delete {selectedIds.size} record{selectedIds.size === 1 ? '' : 's'}? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}>Cancel</Button>
          <Button onClick={handleBulkDeleteConfirmed} color="error" variant="contained" disabled={bulkDeleting}>
            {bulkDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Record</DialogTitle>
        <DialogContent>
          <Typography>
            Permanently delete this {deleteTarget?.operationTypeName} record
            {deleteTarget?.fieldName ? ` on ${deleteTarget.fieldName}` : ''}? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDeleteConfirmed} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OperationsPanel;
